import { useQuery } from "@tanstack/react-query";
import { useAPI } from "../Hooks/useAPIs";
import { useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Alert,
  AlertTitle,
  Typography,
  IconButton,
  Collapse,
  Paper,
} from "@mui/material";
import {
  ChevronRight as ChevronRightIcon,
  ExpandMore as ExpandMoreIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Schedule as ScheduleIcon,
  PlayArrow as PlayArrowIcon,
  HelpOutline as HelpOutlineIcon,
} from "@mui/icons-material";

function getStatusConfig(status) {
  const configs = {
    SUCCESS: { color: "success", icon: CheckCircleIcon },
    FAILURE: { color: "error", icon: CancelIcon },
    PENDING: { color: "warning", icon: ScheduleIcon },
    STARTED: { color: "info", icon: PlayArrowIcon },
    default: { color: "default", icon: HelpOutlineIcon },
  };
  return configs[status] || configs.default;
}

function SubtaskList({ subtasks, level = 0, expandedDetails, toggleDetails }) {
  const [expandedItems, setExpandedItems] = useState({});

  if (!subtasks || subtasks.length === 0) {
    return (
      <Typography
        variant="body2"
        color="text.secondary"
        fontStyle="italic"
        sx={{ py: 1 }}
      >
        No subtasks
      </Typography>
    );
  }

  const toggleExpand = (id) => {
    setExpandedItems((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  return (
    <Box
      sx={{
        ml: level > 0 ? 3 : 0,
        borderLeft: level > 0 ? 2 : 0,
        borderColor: "divider",
        pl: level > 0 ? 2 : 0,
      }}
    >
      {subtasks.map((sub) => {
        const subId = sub.child_task_id || sub.id;
        const subName = sub.child_task_name || "";
        const hasChildren = Array.isArray(sub.subtasks) && sub.subtasks.length > 0;
        const isExpanded = !!expandedItems[subId];
        const isDetailsExpanded = expandedDetails.has(subId);
        const status = sub.child_status || "UNKNOWN";
        const statusConfig = getStatusConfig(status);
        const StatusIcon = statusConfig.icon;

        return (
          <Box key={subId} sx={{ my: 0.5 }}>
            <Paper
              elevation={0}
              sx={{
                display: "flex",
                flexDirection: { xs: "column", sm: "row" },
                alignItems: { xs: "flex-start", sm: "center" },
                gap: 1,
                py: 1,
                px: 1.5,
                borderRadius: 1,
                "&:hover": { bgcolor: "action.hover" },
              }}
            >
              {hasChildren ? (
                <IconButton
                  size="small"
                  onClick={() => toggleExpand(subId)}
                  sx={{ flexShrink: 0 }}
                >
                  {isExpanded ? <ExpandMoreIcon /> : <ChevronRightIcon />}
                </IconButton>
              ) : (
                <Box sx={{ width: 40, display: { xs: "none", sm: "block" } }} />
              )}

              <Box sx={{ flex: 1, minWidth: 0 }}>
                {subName && subName !== "Unknown" && (
                  <Typography variant="body2" fontWeight="medium" color="text.primary" sx={{ mb: 0.5 }}>
                    {subName}
                  </Typography>
                )}

                <Typography
                  variant="caption"
                  component="div"
                  sx={{
                    fontFamily: "monospace",
                    color: "text.secondary",
                    wordBreak: "break-all",
                    cursor: "pointer",
                    "&:hover": { color: "primary.main" },
                  }}
                  onClick={() => toggleDetails(subId)}
                >
                  {subId}
                </Typography>

                <Collapse in={isDetailsExpanded}>
                  <Box sx={{ mt: 1, pl: 1, pt: 0.5, borderLeft: 2, borderColor: "divider" }}>
                    {sub.child_result && (
                      <Box sx={{ mb: 1 }}>
                        <Typography variant="caption" fontWeight="bold" color="text.secondary" sx={{ mb: 0.5, display: "block" }}>
                          Result:
                        </Typography>
                        <Paper elevation={0} sx={{ p: 1, bgcolor: "grey.50", borderRadius: 1, maxHeight: 100, overflow: "auto" }}>
                          <Typography variant="caption" sx={{ fontFamily: "monospace", whiteSpace: "pre-wrap" }}>
                            {sub.child_result}
                          </Typography>
                        </Paper>
                      </Box>
                    )}
                  </Box>
                </Collapse>
              </Box>

              <Chip
                icon={<StatusIcon />}
                label={String(status)}
                color={statusConfig.color}
                size="small"
                sx={{ mt: { xs: 1, sm: 0 } }}
              />
            </Paper>

            {hasChildren && (
              <Collapse in={isExpanded}>
                <SubtaskList
                  subtasks={sub.subtasks}
                  level={level + 1}
                  expandedDetails={expandedDetails}
                  toggleDetails={toggleDetails}
                />
              </Collapse>
            )}
          </Box>
        );
      })}
    </Box>
  );
}

function AllTaskStatusPage() {
  const { apiCall } = useAPI();
  const [expandedDetails, setExpandedDetails] = useState(new Set());
  const [selectedTaskId, setSelectedTaskId] = useState(null);

  const toggleDetails = (taskId) => {
    setExpandedDetails((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(taskId)) newSet.delete(taskId);
      else newSet.add(taskId);
      return newSet;
    });
  };

  const { data: taskList, isLoading: isTaskListLoading, error: taskListError } = useQuery({
    queryKey: ["taskList"],
    queryFn: async () => {
      const res = await apiCall("get", "/api/task-list/");
      return res.data?.tasks ?? [];
    },
  });

  const { data: taskDetails, isLoading: isTaskDetailsLoading, error: taskDetailsError } = useQuery({
    queryKey: ["taskDetails", selectedTaskId],
    queryFn: async () => {
      if (!selectedTaskId) return null;
      const res = await apiCall("get", `/api/task-status/${selectedTaskId}/`);
      return res.data;
    },
    enabled: !!selectedTaskId,
  });

  const handleTaskClick = (taskId) => {
    setSelectedTaskId(taskId);
  };

  return (
    <Box
      sx={{
        height: "85vh",
        maxHeight: "85vh",
        background: "linear-gradient(to bottom right, #f8fafc, #f1f5f9)",
        pt: 3,
        pb: 3,
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Box component="main" sx={{ flex: 1, overflow: "auto" }}>
        {isTaskListLoading && (
          <Box
            sx={{
              height: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexDirection: "column",
              gap: 2,
            }}
          >
            <CircularProgress size={48} />
            <Typography color="text.secondary">Loading tasks...</Typography>
          </Box>
        )}

        {taskListError && (
          <Alert severity="error" sx={{ mb: 3 }}>
            <AlertTitle>Error Loading Tasks</AlertTitle>
            {taskListError.message}
          </Alert>
        )}

        {!isTaskListLoading && taskList && taskList.length === 0 && (
          <Paper
            sx={{
              p: 4,
              textAlign: "center",
              bgcolor: "info.lighter",
              border: 1,
              borderColor: "info.light",
            }}
          >
            <HelpOutlineIcon sx={{ fontSize: 48, color: "info.main", mb: 2 }} />
            <Typography variant="h6" color="info.dark" gutterBottom>
              No tasks found
            </Typography>
            <Typography variant="body2" color="info.dark">
              Tasks will appear here once created
            </Typography>
          </Paper>
        )}

        {!isTaskListLoading && taskList && taskList.length > 0 && (
          <Box
            sx={{
              maxWidth: "xl",
              mx: "auto",
              px: 3,
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                md: "repeat(2, 1fr)",
                lg: "repeat(3, 1fr)",
              },
              gap: 3,
              gridAutoRows: "1fr",
              justifyContent: "center",
            }}
          >
            {taskList.map((task, index) => {
              const taskId = task.root_task_id || `task-${index}`;
              const taskName = task.root_task_name || "";
              const status = task.root_status || "UNKNOWN";
              const versionIds = task.version_ids || [];
              const createdAt = task.created_at || null;
              const isSelected = taskId === selectedTaskId;
              const statusConfig = getStatusConfig(status);
              const StatusIcon = statusConfig.icon;

              return (
                <Box key={taskId} sx={{ display: "flex", flexDirection: "column" }}>
                  <Card
                    elevation={isSelected ? 8 : 3}
                    sx={{
                      height: "100%",
                      display: "flex",
                      flexDirection: "column",
                      transition: "all 0.25s",
                      "&:hover": {
                        transform: "translateY(-4px)",
                        boxShadow: 8,
                        cursor: "pointer",
                      },
                      border: isSelected ? 2 : 0,
                      borderColor: isSelected ? "primary.main" : "transparent",
                    }}
                    onClick={() => handleTaskClick(taskId)}
                  >
                    <Box
                      sx={{
                        bgcolor: `${statusConfig.color}.lighter`,
                        px: 3,
                        py: 2,
                        borderBottom: 1,
                        borderColor: "divider",
                        flexShrink: 0,
                      }}
                    >
                      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
                        <Typography variant="h6" fontWeight="bold">
                          Task
                        </Typography>
                        <Chip icon={<StatusIcon />} label={String(status)} color={statusConfig.color} size="small" />
                      </Box>

                      {taskName && taskName !== "Unknown" && (
                        <Typography variant="body2" fontWeight="medium" sx={{ mb: 1 }}>
                          {taskName}
                        </Typography>
                      )}

                      <Paper elevation={0} sx={{ px: 1, py: 0.5, bgcolor: "background.paper", opacity: 0.9 }}>
                        <Typography
                          variant="caption"
                          sx={{
                            fontFamily: "monospace",
                            wordBreak: "break-all",
                            display: "block",
                          }}
                        >
                          {taskId}
                        </Typography>
                      </Paper>
                    </Box>

                    <CardContent sx={{ flexGrow: 1, overflow: "auto", display: "flex", flexDirection: "column" }}>
                      <Box sx={{ mb: 2, flexShrink: 0 }}>
                        <Typography variant="caption" fontWeight="bold" color="text.secondary" sx={{ textTransform: "uppercase", letterSpacing: 1, mb: 1, display: "block" }}>
                          Versions ({versionIds.length})
                        </Typography>
                        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                          {Array.isArray(versionIds) && versionIds.map((v, idx) => (
                            <Chip key={idx} label={v} size="small" color="primary" variant="outlined" sx={{ height: 24, fontSize: "0.7rem" }} />
                          ))}
                        </Box>
                      </Box>

                      <Box sx={{ mb: 2, pb: 2, borderBottom: 1, borderColor: "divider", flexShrink: 0 }}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                          <ScheduleIcon sx={{ fontSize: 14, color: "text.secondary" }} />
                          <Typography variant="caption" color="text.secondary">
                            {createdAt ? new Date(createdAt).toLocaleString() : "N/A"}
                          </Typography>
                        </Box>
                      </Box>

                      {isSelected && (
                        <Box sx={{ flexGrow: 1, overflow: "auto" }}>
                          {isTaskDetailsLoading && (
                            <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
                              <CircularProgress size={24} />
                            </Box>
                          )}
                          {taskDetailsError && (
                            <Alert severity="error" sx={{ mb: 2 }}>
                              <AlertTitle>Error Loading Task Details</AlertTitle>
                              {taskDetailsError.message}
                            </Alert>
                          )}
                          {taskDetails && (
                            <>
                              {taskDetails.root_result && (
                                <Box sx={{ mb: 2 }}>
                                  <Typography variant="caption" fontWeight="bold" color="text.secondary" sx={{ mb: 0.5, display: "block" }}>
                                    Result:
                                  </Typography>
                                  <Paper elevation={0} sx={{ p: 1, bgcolor: "grey.50", borderRadius: 1, maxHeight: 100, overflow: "auto" }}>
                                    <Typography variant="caption" sx={{ fontFamily: "monospace", whiteSpace: "pre-wrap" }}>
                                      {taskDetails.root_result}
                                    </Typography>
                                  </Paper>
                                </Box>
                              )}
                              <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1.5 }}>
                                Subtasks
                              </Typography>
                              <SubtaskList
                                subtasks={taskDetails.children_statuses || []}
                                expandedDetails={expandedDetails}
                                toggleDetails={toggleDetails}
                              />
                            </>
                          )}
                        </Box>
                      )}
                    </CardContent>
                  </Card>
                </Box>
              );
            })}
          </Box>
        )}
      </Box>
    </Box>
  );
}

export default AllTaskStatusPage;